import { KnowledgeArticle, createArticle } from '../types'

export const darkWebArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'dark-web-overview',
    title: 'Dark Web Monitoring Overview',
    section: 'dark-web',
    summary: 'Monitor your organization\'s domains for credential exposure, data breaches, and info-stealer malware. Get alerts when employee credentials appear on the dark web.',
    content: `
# Dark Web Monitoring

The Dark Web Intelligence module monitors for credential exposure and data breaches affecting your organization.

## Why Monitor the Dark Web?

- **Credential Theft** - Stolen passwords enable account takeover
- **Data Breaches** - Your data may be leaked without your knowledge
- **Info Stealers** - Malware captures credentials from infected machines
- **Supply Chain** - Your vendors' exposure affects you

## Data Sources

### Have I Been Pwned (HIBP)
- Largest breach database
- Email breach lookup
- Stealer log detection
- Paste monitoring

### Hudson Rock
- Info-stealer intelligence
- Real-time credential monitoring
- Leaked URL detection
- Malware family identification

### Additional Sources
- Pastebin monitoring (PSBDMP)
- GitHub code exposure
- Telegram channel monitoring

## Dashboard Metrics

- **Total Exposed** - All credentials found
- **Breached Emails** - Emails in data breaches
- **Stealer Logs** - Credentials from malware
- **Monitored Domains** - Your domains being watched
- **Malware Families** - Types of stealers detected

## Alert Severity

- **Critical** - Active credentials exposed recently
- **High** - Significant exposure requiring action
- **Medium** - Older exposure, monitor situation
- **Low** - Minimal exposure, informational
    `.trim(),
    tags: ['dark-web', 'monitoring', 'breaches', 'credentials', 'info-stealers', 'security'],
    relatedPages: ['/dark-web'],
    icon: 'Skull',
    order: 1,
  }),

  createArticle({
    id: 'dark-web-alerts',
    title: 'Understanding Dark Web Alerts',
    section: 'dark-web',
    summary: 'Alerts show breached emails, stealer logs, leaked URLs, and malware families. Each alert includes affected accounts, breach dates, and recommended actions.',
    content: `
# Understanding Dark Web Alerts

Learn how to interpret and respond to dark web exposure alerts.

## Alert Types

### Breached Emails
Emails found in data breach dumps:
- Which breach contained the email
- Date of the breach
- Data types exposed (passwords, addresses, etc.)
- Total occurrences

### Stealer Logs
Credentials captured by info-stealer malware:
- Malware family (RedLine, Vidar, etc.)
- When captured
- What was stolen
- Machine information

### Leaked URLs
Your URLs found with credentials:
- Login pages discovered
- Credential pairs count
- Employee vs customer exposure
- URL classification

## Investigating Alerts

1. **Identify Scope** - How many accounts affected?
2. **Assess Recency** - When did exposure occur?
3. **Check Data Types** - What was exposed?
4. **Determine Source** - Breach or malware?
5. **Evaluate Risk** - Current password? Active account?

## Response Actions

### Immediate (Critical/High)
1. Force password reset for affected users
2. Enable MFA if not already required
3. Review account activity for compromise
4. Notify affected users

### Short-term (Medium)
1. Assess credential reuse risk
2. Review access logs
3. Update security training
4. Document in incident tracking

### Long-term (Low/Info)
1. Monitor for additional exposure
2. Include in security awareness
3. Update risk assessments
4. Improve detection capabilities

## Malware Families

Common info-stealers you may see:
- **RedLine** - Popular stealer targeting browsers/crypto
- **Vidar** - Steals browser data and screenshots
- **Raccoon** - Subscription-based stealer
- **Lumma** - Modern stealer with 2FA bypass
- **Azorult** - Older but still active stealer
    `.trim(),
    tags: ['dark-web', 'alerts', 'breaches', 'malware', 'response', 'investigation'],
    relatedPages: ['/dark-web'],
    icon: 'AlertTriangle',
    order: 2,
  }),
]
